import {Record, Map} from 'immutable';

import {makeReducer, match} from 'web-app/util/redux';

import type * as Models from './models';
import * as Actions from './actions';

interface SignInModal {
    readonly isVisible: boolean;
    readonly isFetching: boolean;
    readonly hasLocation: boolean;
    readonly distance: number;
    readonly childSignIns: Map<string, Models.ChildFromLocation>;
    readonly employeeSignIns: Models.EmployeeSignIns;
}

export class SignInModalState extends Record<SignInModal>({
    isVisible: false,
    isFetching: false,
    hasLocation: false,
    distance: 0,
    childSignIns: Map(),
    employeeSignIns: [],
}) {}

const updateChildSignIn = (
    state: SignInModalState,
    childId: string,
    newValues: Partial<Models.ChildFromLocation>,
): SignInModalState => {
    const signIn = state.childSignIns.get(childId);

    if (signIn) {
        const updatedSignIn = signIn.merge(newValues);
        const updatedSignIns = state.childSignIns.set(childId, updatedSignIn);
        return state.set('childSignIns', updatedSignIns);
    }

    return state;
};

const updateEmployeeSignIn = (
    state: SignInModalState,
    employeeId: string,
    newValues: Partial<Models.EmployeeFromLocation>,
): SignInModalState => {
    const employeeSignIns = [...state.employeeSignIns];
    const indexOfTargetEmployee = employeeSignIns.findIndex(signIn => signIn.employeeId === employeeId);

    if (indexOfTargetEmployee > -1) {
        employeeSignIns[indexOfTargetEmployee] = {
            ...employeeSignIns[indexOfTargetEmployee],
            ...newValues,
        };
        return state.set('employeeSignIns', employeeSignIns);
    }

    return state;
};

export default makeReducer<SignInModalState>(
    [
        match(Actions.show, state => {
            return state.merge({
                isVisible: true,
            });
        }),
        match(Actions.hide, () => {
            return new SignInModalState();
        }),
        match(Actions.fetchPossibilitiesFromLocation, state => {
            return state.merge({
                isFetching: true,
                hasLocation: false,
                childSignIns: Map(),
                employeeSignIns: [],
            });
        }),
        match(Actions.fetchPossibilitiesFromLocationSuccess, (state, {payload}) => {
            return state.merge({
                isFetching: false,
                childSignIns: payload.response.childSignIns,
                employeeSignIns: payload.response.employeeSignIns,
                hasLocation: true,
                distance: payload.response.distance,
            });
        }),
        match(Actions.fetchPossibilitiesFromLocationFailed, state => {
            return state.merge({
                isFetching: false,
                hasLocation: false,
            });
        }),
        match(Actions.selectPickupPerson, (state, {payload}) => {
            return updateChildSignIn(state, payload.childId, {pickupRelationId: payload.pickupRelationId});
        }),
        match(Actions.signInEmployee, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: true,
            }),
        ),
        match(Actions.signInEmployeeSuccess, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: false,
                checkedIn: true,
                checkedInGroup: payload.group,
            }),
        ),
        match(Actions.signInEmployeeFailed, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: false,
            }),
        ),
        match(Actions.signOutEmployee, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: true,
            }),
        ),
        match(Actions.signOutEmployeeSuccess, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: false,
                checkedIn: false,
                checkedInGroup: null,
            }),
        ),
        match(Actions.signOutEmployeeFailed, (state, {payload}) =>
            updateEmployeeSignIn(state, payload.employeeId, {
                isUpdating: false,
            }),
        ),
        match(Actions.updateSignIn, (state, {payload}) => {
            return updateChildSignIn(state, payload.signIn.childId, {isUpdating: true});
        }),
        match(Actions.updateSignInSuccess, (state, {payload}) => {
            return updateChildSignIn(state, payload.response.childId, {
                isUpdating: false,
                checkedIn: true,
                justSignedInOut: true,
                ...payload.response,
            });
        }),
        match(Actions.updateSignInFailed, (state, {payload}) => {
            return updateChildSignIn(state, payload.childId, {isUpdating: false});
        }),
        match(Actions.signOut, (state, {payload}) => {
            return updateChildSignIn(state, payload.childId, {isUpdating: true});
        }),
        match(Actions.signOutSuccess, (state, {payload}) => {
            return updateChildSignIn(state, payload.response.childId, {
                isUpdating: false,
                checkedIn: false,
                justSignedInOut: true,
                ...payload.response,
            });
        }),
        match(Actions.signOutFailed, (state, {payload}) => {
            return updateChildSignIn(state, payload.childId, {isUpdating: false});
        }),
    ],
    new SignInModalState(),
);
